/**
 * Phase 5.4 — Admin-Daten-Audit-Übersicht (read-only, admin-only).
 *
 * Renderhinweis: bewusst KEINE Chart-Library — die Newton-Designsprache
 * arbeitet mit kompakten Pivot-Tabellen + Inline-Bars (CSS-Width %).
 * Das hält das Bundle klein und passt zum Investmentbank-Pitchbook-Stil.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Download, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("de-DE") : n);

const Bar = ({ value, max, color = "#022750" }) => {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2 bg-navy/5 w-full">
      <div style={{ width: `${pct}%`, backgroundColor: color }} className="h-2"></div>
    </div>
  );
};

const Card = ({ title, hint, children, testid }) => (
  <section data-testid={testid} className="bg-white border border-navy/15 p-5 space-y-4">
    <div>
      <div className="pb-eyebrow">{title}</div>
      {hint && <div className="text-[11px] text-navy/50 font-sans mt-1">{hint}</div>}
    </div>
    {children}
  </section>
);

const csvEscape = (s) => {
  if (s == null) return "";
  const str = String(s);
  return /[,"\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const toCSV = (report) => {
  const rows = [];
  rows.push(["Sektion", "Schlüssel", "Wert"]);
  rows.push(["Allgemein", "Total Investoren", report.total_investoren]);
  rows.push(["Allgemein", "Bericht erstellt", report.generated_at]);
  Object.entries(report.by_quelle || {}).forEach(([k, v]) => rows.push(["Quelle", k, v]));
  Object.entries(report.by_quelle_weitere || {}).forEach(([k, v]) => rows.push(["Quelle (weitere)", k, v]));
  Object.entries(report.by_betreuer || {}).forEach(([k, v]) => rows.push(["Betreuer", k, v]));
  rows.push(["Betreuer", "unzugeordnet", report.betreuer_unzugeordnet]);
  rows.push(["Sperr-Status", "gesperrt_fuer_uns (gesamt)", report.gesperrt_fuer_uns_count]);
  Object.entries(report.gesperrt_fuer_uns_seit || {}).forEach(([k, v]) => rows.push(["Sperr-Status (uns) Verlauf", k, v]));
  rows.push(["Sperr-Status", "gesperrt_fuer_bnp (gesamt)", report.gesperrt_fuer_bnp_count]);
  rows.push(["Sperr-Status", "auch_bei_bnp", report.auch_bei_bnp_count]);
  Object.entries(report.name_consistency || {}).forEach(([k, v]) => rows.push(["Namens-Konsistenz", k, v]));
  Object.entries(report.datenintegrität || {}).forEach(([k, v]) => rows.push(["Datenintegrität", k, v]));
  Object.entries(report.akteur_stats_top10 || {}).forEach(([k, v]) => rows.push(["Akteur-Stats", k, v]));
  return rows.map((r) => r.map(csvEscape).join(",")).join("\n");
};

export default function AdminDatenaudit() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/data-audit");
      setReport(data);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Audit konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const downloadCsv = () => {
    if (!report) return;
    const csv = toCSV(report);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newton-datenaudit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !report) {
    return (
      <div data-testid="admin-audit-page" className="pb-container px-0 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-6">
        <h1 className="font-display font-bold text-navy text-2xl sm:text-[34px]">Daten-Audit</h1>
        <div className="pb-placeholder">Lade Audit-Report …</div>
      </div>
    );
  }

  const maxQuelle = Math.max(0, ...Object.values(report.by_quelle || {}));
  const maxBetreuer = Math.max(0, ...Object.values(report.by_betreuer || {}));
  const nc = report.name_consistency || {};
  const consistent = nc.konsistent;

  return (
    <div data-testid="admin-audit-page" className="pb-container px-0 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="pb-eyebrow">Admin · Read-Only</div>
          <h1 className="font-display font-bold text-navy text-2xl sm:text-[34px] tracking-[-0.02em] mt-2 leading-none">
            Daten-Audit
          </h1>
          <p className="text-sm text-navy/55 font-sans mt-2 max-w-2xl">
            Gesamtüberblick über Datenherkunft, Betreuer-Verteilung, BNP-Sperrungen, Namens-Konsistenz.
            Bericht generiert: <span className="tabular-nums">{report.generated_at?.slice(0, 19).replace("T", " ")}</span> UTC.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="audit-csv" onClick={downloadCsv} className="pb-btn-accent text-xs uppercase tracking-widish font-semibold min-h-[40px]">
            <Download size={13} /> Als CSV exportieren
          </button>
          <button data-testid="audit-refresh" onClick={load} className="pb-btn-outline text-xs uppercase tracking-widish font-semibold min-h-[40px]">
            <RefreshCw size={13} /> Neu laden
          </button>
        </div>
      </div>

      {/* Kennzahlen-Hero */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card title="Investoren gesamt" testid="kpi-total">
          <div className="font-display font-bold text-navy text-4xl tabular-nums">{fmt(report.total_investoren)}</div>
        </Card>
        <Card title="Gesperrt für uns" testid="kpi-uns">
          <div className="font-display font-bold text-tot text-4xl tabular-nums">{fmt(report.gesperrt_fuer_uns_count)}</div>
          <div className="text-[11px] text-navy/55">BNP-Exklusivkontakte + manuelle Sperren</div>
        </Card>
        <Card title="Gesperrt für BNP" testid="kpi-bnp">
          <div className="font-display font-bold text-navy text-4xl tabular-nums">{fmt(report.gesperrt_fuer_bnp_count)}</div>
          <div className="text-[11px] text-navy/55">Heinrichs-Pool, BNP darf nicht aktiv anschreiben</div>
        </Card>
        <Card title="Auch bei BNP" testid="kpi-auch-bnp">
          <div className="font-display font-bold text-gold text-4xl tabular-nums">{fmt(report.auch_bei_bnp_count)}</div>
          <div className="text-[11px] text-navy/55">Doppelte Quellen (Heinrichs + BNP)</div>
        </Card>
      </section>

      {/* Quellen + Betreuer */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Datenquellen-Verteilung" testid="quelle-card" hint="Manche Investoren tauchen in mehreren Quellen auf — Summen ≥ Total.">
          <div className="space-y-2.5">
            {Object.entries(report.by_quelle).map(([k, v]) => (
              <div key={k} data-testid={`quelle-${k}`}>
                <div className="flex justify-between text-xs font-sans mb-1">
                  <span className="text-navy/85">{k}</span>
                  <span className="text-navy font-medium tabular-nums">{fmt(v)}</span>
                </div>
                <Bar value={v} max={maxQuelle} color="#022750" />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Betreuer-Verteilung (intern)" testid="betreuer-card">
          <div className="space-y-2.5">
            {Object.entries(report.by_betreuer).map(([k, v]) => (
              <div key={k} data-testid={`betreuer-${k}`}>
                <div className="flex justify-between text-xs font-sans mb-1">
                  <span className="text-navy/85">{k}</span>
                  <span className="text-navy font-medium tabular-nums">{fmt(v)}</span>
                </div>
                <Bar value={v} max={maxBetreuer} color={k === "BNP" ? "#C4922A" : "#022750"} />
              </div>
            ))}
            {report.betreuer_unzugeordnet > 0 && (
              <div className="text-xs text-tot mt-2">
                ⚠ {report.betreuer_unzugeordnet} Investoren ohne Betreuer-Zuordnung
              </div>
            )}
          </div>
        </Card>
      </section>

      {/* Sperr-Historie */}
      <Card title={'Sperr-Historie „gesperrt für uns"'} testid="sperr-history" hint="Aggregiert nach Stand-Datum aus gesperrt_grund.">
        <div className="space-y-1.5 text-sm font-sans">
          {Object.entries(report.gesperrt_fuer_uns_seit).map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-navy/10 py-1.5">
              <span className="text-navy/80 tabular-nums">{k}</span>
              <span className="text-navy font-medium tabular-nums">{fmt(v)}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Namens-Konsistenz */}
      <Card title={'Namens-Konsistenz „Heinrich → Heinrichs"'} testid="naming-card">
        <div className={`flex items-start gap-3 px-3 py-2.5 border-l-2 ${consistent ? "border-heiss bg-heiss/5" : "border-tot bg-tot/5"}`}>
          {consistent ? <CheckCircle2 size={18} className="text-heiss shrink-0 mt-0.5" strokeWidth={2}/> : <AlertTriangle size={18} className="text-tot shrink-0 mt-0.5" strokeWidth={2}/>}
          <div className="text-sm font-sans text-navy">
            {consistent ? (
              <><strong>Konsistent.</strong> Keine alten „Heinrich"-Einträge mehr in der Datenbank.</>
            ) : (
              <><strong>Inkonsistenz erkannt.</strong> Es gibt noch alte „Heinrich"-Einträge (ohne „s"). Bitte erneute Migration ausführen.</>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs font-sans mt-3">
          <div className="flex justify-between"><span className="text-navy/70">investors.betreuer_intern „Heinrich" (alt):</span> <span className="font-medium tabular-nums">{nc.betreuer_intern_heinrich_alt}</span></div>
          <div className="flex justify-between"><span className="text-navy/70">investors.betreuer_intern „Heinrichs" (korrekt):</span> <span className="font-medium tabular-nums">{nc.betreuer_intern_heinrichs_korrekt}</span></div>
          <div className="flex justify-between"><span className="text-navy/70">activity_log „Heinrich" (alt):</span> <span className="font-medium tabular-nums">{nc.akteur_heinrich_alt}</span></div>
          <div className="flex justify-between"><span className="text-navy/70">activity_log „Heinrichs" (pre-Auth, jetzt migriert):</span> <span className="font-medium tabular-nums">{nc.akteur_heinrichs_pre_auth}</span></div>
          <div className="flex justify-between"><span className="text-navy/70">activity_log „Andreas Heinrichs" (post-Auth):</span> <span className="font-medium tabular-nums">{nc.akteur_andreas_heinrichs_post_auth}</span></div>
          <div className="flex justify-between"><span className="text-navy/70">Admin-User „Andreas Heinrichs" existiert:</span> <span className="font-medium">{nc.user_anzeigename_andreas_heinrichs ? "Ja" : "Nein"}</span></div>
        </div>
      </Card>

      {/* Datenintegrität + Akteur-Stats */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Datenintegrität" testid="integritaet-card">
          <div className="space-y-1.5 text-sm font-sans">
            <div className="flex justify-between border-b border-navy/10 py-1.5">
              <span className="text-navy/80">Ohne created_at</span>
              <span className="text-navy font-medium tabular-nums">{fmt(report.datenintegrität?.ohne_created_at)}</span>
            </div>
            <div className="flex justify-between border-b border-navy/10 py-1.5">
              <span className="text-navy/80">Ohne Quelle</span>
              <span className="text-navy font-medium tabular-nums">{fmt(report.datenintegrität?.ohne_quelle)}</span>
            </div>
            <div className="flex justify-between border-b border-navy/10 py-1.5">
              <span className="text-navy/80">Potenzielle Duplikate (firma_normalized)</span>
              <span className="text-navy font-medium tabular-nums">{(report.duplikate_potenziell || []).length}</span>
            </div>
          </div>
          {(report.duplikate_potenziell || []).length > 0 && (
            <details className="mt-3">
              <summary className="text-xs uppercase tracking-widish text-gold cursor-pointer">Duplikate auflisten</summary>
              <div className="text-[11px] font-mono text-navy/70 mt-2 space-y-1">
                {report.duplikate_potenziell.map((d) => (
                  <div key={d.firma_normalized}>
                    <span className="text-navy">{d.firma_normalized}</span> · {d.count}× ·{" "}
                    <span className="italic">{d.firmen.join(" / ")}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </Card>

        <Card title="Activity-Log: Top-Akteure" testid="akteur-card">
          <div className="space-y-1.5 text-sm font-sans">
            {Object.entries(report.akteur_stats_top10).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-navy/10 py-1.5">
                <span className="text-navy/85">{k}</span>
                <span className="text-navy font-medium tabular-nums">{fmt(v)}</span>
              </div>
            ))}
          </div>
        </Card>
      </section>
    </div>
  );
}
